// WHAT A COURSE MODULE IS MADE OF — five block kinds and their fields.
//
// Same shape and the same reasoning as websiteSectionKinds.js: one definition
// drives the editor, the word count, and (when the drafting chat lands) what
// Brill is told and what its reply is parsed back into. A field added here needs
// no change anywhere else.
//
// FIVE KINDS, NOT THE MOCKUP'S SIX. The archetype note is NOT a block — her call,
// 2026-08-17. Every block here is what the STUDENT gets: what she leaves with,
// how the session runs, what the group works through, what she does next, what
// she brings. The archetype line is a note to the COACH about how to run it, so
// ordering it alongside the content means it can end up in the deliverable. It
// lives at the top of a module instead, as a reminder.
// This isn't a new opinion — My Offer's curriculum prompt already says
// "Archetype filters delivery suggestions naturally — woven in where relevant,
// never a separate field."
//
// A MODULE IS NOT A WEEK. Weeks came out entirely: modules are ordered, not
// dated, and when she runs one is decided wherever the course is hosted.

/* Field shape is websiteSectionKinds' shape exactly — key / label / type / guide,
   plus `parts` for a list of objects and `block` for the label Brill states it
   under. See that file's header for why the label rule is what it is. */
export const COURSE_BLOCK_KINDS = [
  {
    id: "outcome",
    label: "Outcome",
    hint: "What they leave with",
    fields: [
      /* "She has X built", never "she will understand X" — the curriculum prompt
         insists every outcome is operational, and the guidance says so here so
         the same rule survives into the module she edits by hand. */
      { key: "text", label: "Outcome", block: "OUTCOME", type: "prose", guide: "what they leave with — built, done, in hand" },
    ],
  },
  {
    id: "outline",
    label: "Teaching outline",
    hint: "Hook, core teaching, application",
    fields: [
      {
        /* The one genuinely nested thing in Momentum: a segment can hold its own
           points, each with its own minutes. Stored as `points` inside the row so
           a segment without any is simply a segment with an empty list. */
        key: "segments", label: "Segments", block: "SEGMENT", type: "list", itemLabel: "Segment",
        parts: [
          { key: "label", label: "Segment", type: "line", guide: "e.g. Opening hook, Core teaching, Application" },
          { key: "minutes", label: "Minutes", type: "line", guide: "how long it runs" },
          { key: "body", label: "What happens", type: "prose", guide: "what you say and do in it" },
        ],
      },
    ],
  },
  {
    id: "questions",
    label: "Discussion questions",
    hint: "What the group works through",
    fields: [
      { key: "items", label: "Questions", block: "QUESTION", type: "list", itemLabel: "Question", guide: "one question, as you'd ask it out loud" },
    ],
  },
  {
    id: "homework",
    label: "Homework",
    hint: "What they do before the next module",
    fields: [
      /* The curriculum rule: homework produces the module's outcome or sets up
         the next one. Never busywork. */
      { key: "text", label: "Homework", block: "HOMEWORK", type: "prose", guide: "the one thing that produces this outcome" },
    ],
  },
  {
    id: "prework",
    label: "Pre-work",
    hint: "What they read or bring",
    fields: [
      { key: "text", label: "Pre-work", block: "PREWORK", type: "prose", guide: "what they do before the session" },
    ],
  },
];

const BY_ID = new Map(COURSE_BLOCK_KINDS.map((k) => [k.id, k]));

/** The definition for a block kind, or null. Unlike website sections there is no
 *  catch-all kind to fall back to — a module's blocks come from this fixed five,
 *  so an id that isn't one of them is a row to skip, not a box to guess at. */
export function blockKind(id) {
  return BY_ID.get(String(id || "").toLowerCase()) || null;
}

export function blockLabelFor(id) {
  return blockKind(id)?.label || "";
}

/** The blocks this module has, in her order, as definitions. Anything stored that
 *  isn't a real kind is dropped rather than rendered as an unnamed box. */
export function blocksOf(module) {
  const order = Array.isArray(module?.blockOrder) ? module.blockOrder : [];
  return order.map(blockKind).filter(Boolean);
}

/** The kinds this module doesn't have yet — what the Add block menu offers. */
export function missingBlocks(module) {
  const have = new Set((Array.isArray(module?.blockOrder) ? module.blockOrder : []));
  return COURSE_BLOCK_KINDS.filter((k) => !have.has(k.id));
}

/* Where each block's content lives on the row.
   `lesson_plans` had real columns before the kinds existed, and they are kept:
   `outcome` and `homework` are text, `teaching_outline` and `discussion_questions`
   are jsonb. So a block reads and writes its own column rather than everything
   being packed into one blob — which also means the columns the Accelerator seed
   fills are the columns the editor shows. */
const COLUMN = {
  outcome: "outcome",
  outline: "teachingOutline",
  questions: "discussionQuestions",
  homework: "homework",
  prework: "prework",
};

/** The stored value for one block of a module, in the shape its fields expect. */
export function blockContent(module, id) {
  const kind = blockKind(id);
  if (!kind) return {};
  const value = module?.[COLUMN[id]];
  const field = kind.fields[0];
  /* Single-field blocks live in a plain text column, so they're lifted into
     `{ [key]: value }` for the field renderer. List blocks are already arrays. */
  if (field.type === "list") return { [field.key]: Array.isArray(value) ? value : [] };
  return { [field.key]: typeof value === "string" ? value : "" };
}

/** A module patched with one block's edited content, ready to save. */
export function withBlockContent(module, id, content) {
  const kind = blockKind(id);
  if (!kind) return module;
  const field = kind.fields[0];
  const value = content?.[field.key];
  return {
    ...module,
    [COLUMN[id]]: field.type === "list" ? (Array.isArray(value) ? value : []) : String(value ?? ""),
  };
}

/* Words, counted the way a person counts them — the same rule as the website's,
   so the two panels' Words columns mean the same thing. */
export function wordCount(text) {
  return String(text || "")
    .replace(/[#*_`>|-]/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;
}

/** Words in ONE block. The collapsed block header shows this, and the module
 *  total is the sum of them — one traversal defined once, so a header and the
 *  table's Words column can never disagree about what counts. */
export function blockWords(module, id) {
  const kind = blockKind(id);
  if (!kind) return 0;
  const content = blockContent(module, id);
  let n = 0;
  for (const field of kind.fields) {
    const value = content[field.key];
    if (Array.isArray(value)) {
      for (const row of value) {
        if (row && typeof row === "object") {
          for (const part of (field.parts || [])) {
            // Minutes are a number, not prose — counting "45" as a word would
            // make a timing look like content.
            if (part.key !== "minutes") n += wordCount(row[part.key]);
          }
        } else {
          n += wordCount(row);
        }
      }
    } else {
      n += wordCount(value);
    }
  }
  return n;
}

/** Every word in a module, across every block it has. */
export function moduleWords(module) {
  return blocksOf(module).reduce((n, kind) => n + blockWords(module, kind.id), 0);
}

/** The minutes ONE block accounts for, or null when it isn't a timed one.
 *  Only the teaching outline carries timings, and knowing which kind that is
 *  belongs here beside the kinds rather than as an id typed into the editor. */
export function blockMinutes(module, id) {
  return id === "outline" ? moduleMinutes(module) : null;
}

/* Whitespace-collapsed, because a preview sits on ONE line — a stored newline
   would otherwise render as a gap in the middle of the row. */
function oneLine(value) {
  return String(value ?? "").replace(/\s+/g, " ").trim();
}

/** A one-line taste of a block, for its row when it's collapsed. The first PROSE
 *  it holds, because that's the sentence she'd recognise it by — a segment's
 *  label ("Opening hook") names the shape of the block, not what's in it.
 *  Empty when nothing is written, which is what makes an unwritten block read as
 *  unwritten rather than as one she can't see into. */
export function blockPreview(module, id) {
  const kind = blockKind(id);
  if (!kind) return "";
  const content = blockContent(module, id);
  for (const field of kind.fields) {
    const value = content[field.key];
    if (!Array.isArray(value)) {
      const line = oneLine(value);
      if (line) return line;
      continue;
    }
    for (const row of value) {
      if (!row || typeof row !== "object") {
        const line = oneLine(row);
        if (line) return line;
        continue;
      }
      const parts = field.parts || [];
      /* Prose first, then the rest in their own order. Minutes never preview:
         a row reading "25" tells her nothing about what the segment is. */
      const ordered = [
        ...parts.filter((p) => p.type === "prose"),
        ...parts.filter((p) => p.type !== "prose" && p.key !== "minutes"),
      ];
      for (const part of ordered) {
        const line = oneLine(row[part.key]);
        if (line) return line;
      }
    }
  }
  return "";
}

/** How long the module runs, from its segments. Null when nothing is timed —
 *  "0m" would claim a module takes no time rather than that nobody has said. */
export function moduleMinutes(module) {
  const segments = Array.isArray(module?.teachingOutline) ? module.teachingOutline : [];
  let total = 0;
  let any = false;
  for (const s of segments) {
    const n = parseInt(String(s?.minutes || "").replace(/[^0-9]/g, ""), 10);
    if (Number.isFinite(n)) { total += n; any = true; }
  }
  return any ? total : null;
}

/** Where a module stands, for the table's dot and the counts above it.
 *  Three states, the same vocabulary the website table uses. */
export function moduleStatus(module) {
  if (module?.status === "done") return "done";
  return moduleWords(module) > 0 ? "draft" : "empty";
}

export const MODULE_STATUS_LABEL = { done: "Done", draft: "Draft", empty: "Not written" };

/* ── What Brill is told, and what she writes back ─────────────────────────────
   The drafting chat and the one-click draft both need the field list as LINES:
   the exact labels to answer under, and what each one is for. Built from the
   kinds above rather than written into api/_lib/prompts.js, because api/ is CommonJS
   and can't import this — a copy there would drift the first time a field
   changed. Same rule, and the same reason, as websiteSectionKinds.js.

   These carry `course` in their names on purpose: MomentumHub imports the
   website kinds' identically-shaped helpers, and two `fieldsOf` in one file is
   how the wrong spec ends up driving the wrong parser.                        */

/** The fields of one block kind. Empty for an id that isn't one of the five. */
export function courseFieldsOf(id) {
  return blockKind(id)?.fields || [];
}

/** A field holding rows rather than one value. */
export function isCourseListField(field) {
  return field?.type === "list";
}

/** The label Brill states this field under. */
export function courseFieldLabel(field) {
  return String(field?.block || field?.key || "").toUpperCase();
}

/** The rows of a list field, always an array. */
export function courseRowsOf(content, field) {
  const v = content?.[field?.key];
  return Array.isArray(v) ? v : [];
}

/**
 * The field list for one kind, as the lines Brill reads and writes back.
 *
 * List fields are stated as ONE label repeated per row, parts separated by
 * ` | ` — a repeated label with a fixed separator is the shape a model gets
 * right most often. Indexed labels (`SEGMENT_1_BODY`) invite it to invent a
 * fourth segment and renumber the rest when it rewrites one.
 */
export function courseSpecLines(id) {
  const lines = [];
  for (const f of courseFieldsOf(id)) {
    const item = (f.itemLabel || f.label).toLowerCase();
    if (!isCourseListField(f)) {
      lines.push(`${courseFieldLabel(f)}: ${f.label.toLowerCase()} — ${f.guide}`);
    } else if (!f.parts) {
      lines.push(`${courseFieldLabel(f)}: one ${item} — ${f.guide}. Repeat this line for each one.`);
    } else {
      const parts = f.parts.map((p) => `${p.label.toLowerCase()} (${p.guide})`).join(" | ");
      lines.push(`${courseFieldLabel(f)}: ${parts}. Separate the parts with " | ", and repeat this line for each ${item}.`);
    }
  }
  return lines;
}
